import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import LockClockOutlinedIcon from "@mui/icons-material/LockClockOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { MODULES, canAccessModule } from "../../utils/moduleAccess";

const modules = [
  {
    title: "Logistics Management",
    description: "Manage logistics operations and movement of goods.",
    icon: LocalShippingOutlinedIcon,
    comingSoon: true,
    color: "#0f766e",
    background: "#ccfbf1",
  },
  {
    title: "Approval Management",
    description: "Manage requests, reviews, and approval workflows.",
    icon: FactCheckOutlinedIcon,
    comingSoon: false,
    key: "approval",
    module: MODULES.APPROVAL,
    actionLabel: "Open Approval Management",
    color: "#7c3aed",
    background: "#ede9fe",
  },
  {
    title: "Material Tracker",
    description: "Track material availability, shortages, requisitions, and purchase orders.",
    icon: Inventory2OutlinedIcon,
    comingSoon: false,
    key: "materialTracker",
    module: MODULES.MATERIAL_TRACKER,
    actionLabel: "Open Material Tracker",
    color: "#4338ca",
    background: "#e0e7ff",
  },
  {
    title: "New Product Development",
    description: "Manage new product development activities and progress.",
    icon: PrecisionManufacturingOutlinedIcon,
    comingSoon: false,
    key: "npd",
    module: MODULES.NPD,
    actionLabel: "Open New Product Development",
    color: "#b45309",
    background: "#fef3c7",
  },
];

const ModuleLanding = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const username = user?.username || "";
  const role = user?.userRole?.[0] || "";

  const openMaterialTracker = () => {
    // Admin users enter the existing admin area; tracker users enter its dashboard.
    if (role === "ADMIN") {
      navigate("/admin");
    } else {
      navigate("/dashboard");
    }
  };

  const openApprovalManagement = () => {
    // eApproval admins enter its admin area; all other roles enter its dashboard.
    if (role === "ADMIN") {
      navigate("/approval/admin");
    } else {
      navigate("/approval/dashboard");
    }
  };

  const openModule = (module) => {
    if (module.key === "approval") {
      openApprovalManagement();
    } else if (module.key === "npd") {
      // NPD opens straight on the dashboard for the user's role
      navigate(role === "ADMIN" ? "/npd/admin" : "/npd/dashboard");
    } else {
      openMaterialTracker();
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f8fafc 0%, #f5f3ff 52%, #f8fafc 100%)",
        py: { xs: 5, md: 9 },
        px: 2,
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ mb: { xs: 4, md: 6 }, textAlign: "center" }}>
          <Typography
            component="h1"
            sx={{
              color: "#111827",
              fontSize: { xs: 30, md: 42 },
              fontWeight: 700,
              lineHeight: 1.2,
              mb: 1.5,
            }}
          >
            Choose a module
          </Typography>
          {username && (
            <Typography sx={{ color: "#475569", fontSize: 13, mt: 2 }}>
              Signed in as {username}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            gap: 2.5,
          }}
        >
          {modules.map((module) => {
            const Icon = module.icon;
            // A card opens only when the module is live and the user's role has access to it.
            const noAccess = !module.comingSoon && !canAccessModule(role, module.module);
            const locked = module.comingSoon || noAccess;

            return (
              <Card
                key={module.title}
                onClick={locked ? undefined : () => openModule(module)}
                elevation={0}
                sx={{
                  border: "1px solid",
                  borderColor: locked ? "#e2e8f0" : "#c7d2fe",
                  borderRadius: 4,
                  backgroundColor: "rgba(255,255,255,0.94)",
                  minHeight: 220,
                  cursor: locked ? "default" : "pointer",
                  transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
                  "&:hover": locked
                    ? {}
                    : {
                        transform: "translateY(-4px)",
                        borderColor: "#818cf8",
                        boxShadow: "0 18px 36px rgba(79,70,229,0.12)",
                      },
                }}
              >
                <CardContent sx={{ p: { xs: 2.5, md: 3.5 }, "&:last-child": { pb: { xs: 2.5, md: 3.5 } } }}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 54,
                        height: 54,
                        borderRadius: 3,
                        backgroundColor: module.background,
                        color: module.color,
                        flexShrink: 0,
                      }}
                    >
                      <Icon sx={{ fontSize: 29 }} />
                    </Box>
                    {locked ? (
                      <Chip
                        icon={noAccess ? <LockOutlinedIcon sx={{ "&&": { fontSize: 15 } }} /> : <LockClockOutlinedIcon sx={{ "&&": { fontSize: 15 } }} />}
                        label={noAccess ? "No Access" : "Coming Soon"}
                        size="small"
                        sx={{
                          height: 26,
                          backgroundColor: "#f1f5f9",
                          color: "#64748b",
                          fontWeight: 600,
                          fontSize: 11,
                          "& .MuiChip-icon": { color: "#64748b" },
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "50%",
                          backgroundColor: "#eef2ff",
                          color: "#4338ca",
                        }}
                      >
                        <ArrowForwardRoundedIcon fontSize="small" />
                      </Box>
                    )}
                  </Stack>

                  <Typography sx={{ mt: 3, color: "#111827", fontSize: 20, fontWeight: 700 }}>
                    {module.title}
                  </Typography>
                  <Typography sx={{ mt: 1, color: "#64748b", fontSize: 14, lineHeight: 1.7 }}>
                    {module.description}
                  </Typography>
                  {!locked && (
                    <Typography sx={{ mt: 2.5, color: "#4338ca", fontSize: 13, fontWeight: 700 }}>
                      {module.actionLabel}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Box>
        <Typography sx={{ color: "#94a3b8", fontSize: 12, textAlign: "center", mt: 5 }}>
          ProcureTrack Workspace
        </Typography>
      </Container>
    </Box>
  );
};

export default ModuleLanding;
